import {
  BadgeCheck,
  Banknote,
  BrainCircuit,
  Briefcase,
  FileCheck2,
  Gauge,
  Handshake,
  Network,
  Trophy,
  Users,
} from "lucide-react";

import { SiteShell } from "@/components/site/SiteShell";
import { BenefitGrid } from "@/components/landing/BenefitGrid";
import { CalculatorBand } from "@/components/landing/CalculatorBand";
import { FinalCta } from "@/components/landing/FinalCta";
import { LandingHero } from "@/components/landing/LandingHero";
import { StickyMobileCta } from "@/components/landing/StickyMobileCta";
import { WhyTtite } from "@/components/landing/WhyTtite";

const SLUG = "financial-statement-fees";
const SERVICE = "financial-statements";
const ACCENT = "#1f3a5f";
const CTA = "Get my estimate";

const CREDENTIALS = [
  {
    icon: BadgeCheck,
    title: "Chartered Accountants (SA)",
    text: "Statements prepared and reviewed with CA(SA) expertise behind them.",
  },
  {
    icon: Briefcase,
    title: "Real-world finance experience",
    text: "Prepared by people who have run finance functions.",
  },
  {
    icon: FileCheck2,
    title: "Audit experience",
    text: "Prepared by people who have sat on the other side of an audit file.",
  },
];

const WHY_IT_MATTERS = [
  {
    icon: Banknote,
    title: "Accessing funding",
    text: "Often the first document a bank, funder or grant programme asks to see.",
  },
  {
    icon: BrainCircuit,
    title: "Making better decisions",
    text: "A clear view of the year behind you, to price, spend and hire against.",
  },
  {
    icon: Users,
    title: "Attracting investors",
    text: "Give potential investors a clearer view of how your business has performed.",
  },
  {
    icon: FileCheck2,
    title: "Tax & CIPC compliance",
    text: "Support your tax reporting and applicable company compliance requirements.",
  },
  {
    icon: Trophy,
    title: "Tenders & opportunities",
    text: "Financial statements are often requested for tenders and larger business opportunities.",
  },
];

const WHY_TTITE = [
  {
    icon: Gauge,
    title: "Quick turnaround",
    text: "We work to get your statements out promptly, and flag anything missing early rather than at the deadline.",
  },
  {
    icon: Network,
    title: "Group structures welcome",
    text: "Comfortable working with consolidations, intercompany balances, loan accounts and multi-entity structures.",
  },
  {
    icon: Handshake,
    title: "Commercial understanding",
    text: "We understand how financial information is used by funders, investors and decision-makers — and prepare your statements with that broader context in mind.",
  },
];

/** Paid-traffic landing page for annual financial statement preparation. */
export default function FinancialStatementFeesPage() {
  return (
    <SiteShell testId="financial-statement-fees-page">
      <div style={{ "--tt-accent": ACCENT }}>
        <LandingHero
          slug={SLUG}
          service={SERVICE}
          eyebrow="Annual financial statements"
          heading="Financial statements that build"
          highlight="business credibility"
          lead="Professional annual financial statements prepared from your accounting records to support your reporting requirements and give stakeholders a clearer view of your business."
          ctaLabel="Get my financial statement estimate"
          note="An indicative estimate in about 2 minutes. No obligation."
          credentials={CREDENTIALS}
        />

        <BenefitGrid
          slug={SLUG}
          centreLastRow
          heading="Statements that do more than keep you compliant"
          lead="Credible financial information can do more than meet a reporting requirement. It can support funding, better decisions, investment and new business opportunities."
          items={WHY_IT_MATTERS}
        />

        <CalculatorBand
          slug={SLUG}
          service={SERVICE}
          heading="Know the cost before you start"
          text="Your estimate depends on your turnover, the number of financial years you need and the state of your accounting records. Answer a few questions and get your indicative estimate."
          ctaLabel={CTA}
          fineprint="Indicative estimate only — the final fee is confirmed once we understand the required scope."
        />

        <WhyTtite slug={SLUG} heading="Why trust us with your year-end" items={WHY_TTITE} />

        <FinalCta
          slug={SLUG}
          service={SERVICE}
          heading="Get your financial statements sorted"
          text="See what your year-end could cost, then decide."
          ctaLabel={CTA}
          secondary="call"
        />

        <StickyMobileCta slug={SLUG} service={SERVICE} label="Calculate fees" />
      </div>
    </SiteShell>
  );
}
